import PropTypes from "prop-types";
// material-ui
import { styled } from "@mui/material/styles";
import {
  Avatar,
  Box,
  createTheme,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import PaidIcon from "@mui/icons-material/Paid";
// project imports
import MainCard from "../cards/MainCard";
import SkeletonExpectedTotalContributionLightCard from "../cards/skeleton/SkeletonExpectedTotalContributionLightCard";

// assets
import { colors } from "../../utility/types/constant";
import { formatCurrency } from "../../utility/currencyUtils";

declare module "@mui/material/styles" {
  interface PaletteColor {
    800?: string;
  }
  interface SimplePaletteColorOptions {
    800?: string;
  }

  interface TypographyVariants {
    commonAvatar: React.CSSProperties;
    smallAvatar: React.CSSProperties;
    mediumAvatar: React.CSSProperties;
    largeAvatar: React.CSSProperties;
  }

  // Allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    commonAvatar?: React.CSSProperties;
    smallAvatar?: React.CSSProperties;
    mediumAvatar: React.CSSProperties;

    largeAvatar?: React.CSSProperties;
  }
}

const theme = createTheme({
  palette: {
    secondary: {
      main: "#f50057",
      dark: "#c51162",
      800: "#9c0045", // Your custom numeric shade
    },
  },
});

// styles
const CardWrapper = styled(MainCard)(({ theme }) => ({
  overflow: "hidden",
  position: "relative",
  "&:after": {
    content: '""',
    position: "absolute",
    width: 210,
    height: 210,
    background: `linear-gradient(210.04deg, ${colors.mediumGold} -50.94%, rgba(144, 202, 249, 0) 83.49%)`,
    borderRadius: "50%",
    top: -30,
    right: -180,
  },
  "&:before": {
    content: '""',
    position: "absolute",
    width: 210,
    height: 210,
    background: `linear-gradient(140.9deg, ${colors.mediumGold} -14.02%, rgba(144, 202, 249, 0) 70.50%)`,
    borderRadius: "50%",
    top: -160,
    right: -130,
  },
}));
export interface ExpectedTotalContributionLightCardProps {
  isLoading: boolean;
  expectedTotalContribution: string;
  currency?: string;
}

const ExpectedTotalContributionLightCard = (
  props: ExpectedTotalContributionLightCardProps
) => {
  const { isLoading, expectedTotalContribution, currency = "usd" } = props;

  return (
    <>
      {isLoading ? (
        <SkeletonExpectedTotalContributionLightCard />
      ) : (
        <CardWrapper border={false} content={false}>
          <Box sx={{ p: 2 }}>
            <List sx={{ py: 0 }}>
              <ListItem alignItems="center" disableGutters sx={{ py: 0 }}>
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    sx={{
                      ...theme.typography.commonAvatar,
                      ...theme.typography.largeAvatar,
                      backgroundColor: colors.mainPrimary,
                      color: colors.white,
                    }}
                  >
                    <PaidIcon fontSize="inherit" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  sx={{
                    py: 0,
                    mt: 0.45,
                    mb: 0.45,
                  }}
                  primary={
                    <Typography variant="h4">
                      {formatCurrency(Number(expectedTotalContribution), currency)}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: colors.secondary,
                        mt: 0.5,
                        fontSize: "1.2rem",
                        fontWeight: 500,
                      }}
                    >
                      Expected Total Contribution
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>
        </CardWrapper>
      )}
    </>
  );
};

ExpectedTotalContributionLightCard.propTypes = {
  isLoading: PropTypes.bool,
  expectedTotalContribution: PropTypes.string,
};

export default ExpectedTotalContributionLightCard;
